import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * Discreet inline save-feedback used wherever a mutation has no other visible
 * acknowledgement — lead notes (blur), team role change, guardrail toggle
 * (F099/A18) and the admin CAC input (F103/A25).
 *
 * Plane-agnostic: it imports NO api client, so it serves both the tenant plane
 * (``apiFetch``/``ApiError``) and the admin plane (``adminApiFetch``/
 * ``AdminApiError``). Consumers compute the human ``errorMessage`` themselves
 * (typically via ``apiErrorMessage``) and wire ``onRetry`` to re-run the save.
 */

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

interface SaveStatusProps {
  status: SaveState;
  /** Pre-translated error message; falls back to a generic label when absent. */
  errorMessage?: string;
  onRetry?: () => void;
  className?: string;
}

export function SaveStatus({ status, errorMessage, onRetry, className }: SaveStatusProps) {
  const { t } = useTranslation();

  if (status === 'idle') return null;

  if (status === 'saving') {
    return (
      <span
        role="status"
        className={cn('inline-flex items-center gap-1.5 text-xs text-muted-foreground', className)}
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        {t('common.save_status.saving')}
      </span>
    );
  }

  if (status === 'saved') {
    return (
      <span
        role="status"
        className={cn('inline-flex items-center gap-1.5 text-xs text-success', className)}
      >
        <Check className="h-3.5 w-3.5" aria-hidden="true" />
        {t('common.save_status.saved')}
      </span>
    );
  }

  // error
  return (
    <span
      role="alert"
      className={cn('inline-flex items-center gap-2 text-xs text-destructive', className)}
    >
      <span>{errorMessage ?? t('common.save_status.error')}</span>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="font-medium underline underline-offset-2 hover:no-underline"
        >
          {t('common.save_status.retry')}
        </button>
      )}
    </span>
  );
}

interface UseSaveStatusOptions {
  /** How long "Salvo ✓" stays visible before returning to idle (ms). */
  savedResetMs?: number;
}

interface UseSaveStatusReturn {
  status: SaveState;
  /** The raw thrown value from the last failed save (for ``apiErrorMessage``). */
  error: unknown;
  /** Run an async mutation, driving the status machine around it. */
  save: (fn: () => Promise<unknown>) => Promise<void>;
  /** Re-run the most recent ``save`` fn (wired to the SaveStatus retry button). */
  retry: () => Promise<void>;
  /** Force back to idle (e.g. when the host form unmounts/closes). */
  reset: () => void;
}

/**
 * State machine behind {@link SaveStatus}: ``idle → saving → saved → idle`` on
 * success (auto-reset after ``savedResetMs``), ``saving → error`` on failure
 * with the thrown value retained for the caller to translate + a ``retry``.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useSaveStatus({
  savedResetMs = 1500,
}: UseSaveStatusOptions = {}): UseSaveStatusReturn {
  const [status, setStatus] = useState<SaveState>('idle');
  const [error, setError] = useState<unknown>(null);
  const lastFn = useRef<(() => Promise<unknown>) | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const clearTimer = () => {
    if (timer.current !== undefined) clearTimeout(timer.current);
  };

  const save = useCallback(
    async (fn: () => Promise<unknown>) => {
      lastFn.current = fn;
      clearTimer();
      setError(null);
      setStatus('saving');
      try {
        await fn();
        setStatus('saved');
        timer.current = setTimeout(() => setStatus('idle'), savedResetMs);
      } catch (e) {
        setError(e);
        setStatus('error');
      }
    },
    [savedResetMs]
  );

  const retry = useCallback(async () => {
    if (lastFn.current) await save(lastFn.current);
  }, [save]);

  const reset = useCallback(() => {
    clearTimer();
    setError(null);
    setStatus('idle');
  }, []);

  // Clear any pending reset timer on unmount.
  useEffect(() => () => clearTimer(), []);

  return { status, error, save, retry, reset };
}
